// NOM-035 — Informe PDF (numeral 7.7): plantilla HTML → Chromium headless (backend)
// Reemplaza el dibujo manual con jsPDF por un documento HTML/CSS de impresión real,
// que el backend renderiza a PDF con Playwright (page.pdf()) — texto vectorial,
// paginación nativa vía @page/page-break, sin rasterizar nada.
//
// Nota de paginación: a diferencia del bosquejo original (que fijaba cada .page a
// 8.5x11in con overflow:hidden, recortando cualquier exceso), aquí generamos varias
// .page por sección larga (dominios, programa) en lotes conservadores y dejamos
// overflow:visible como red de seguridad — si un lote se queda corto de espacio,
// el contenido se desborda visualmente en vez de desaparecer silenciosamente.

import { DOM_INTERPRETACION, DOM_RECOMENDACIONES } from '../data/dom-textos';
import { calcularMuestraGIII, determinarNivel } from './scoring';
import {
  NIVELES_RIESGO_GII, NIVELES_CATEGORIA_GII, NIVELES_DOMINIO_GII,
  NIVELES_RIESGO_GIII, NIVELES_CATEGORIA_GIII, NIVELES_DOMINIO_GIII,
} from '../data/baremos';
import OWL_LOGO_B64 from '../assets/owl-logo-base64.txt?raw';

const NIVEL_ARR = ['Nulo', 'Bajo', 'Medio', 'Alto', 'Muy alto'];
const NIVEL_COLOR = { Nulo: '#3E8E6E', Bajo: '#88A23F', Medio: '#D9A441', Alto: '#C9722E', 'Muy alto': '#B23A36' };
const NIVEL_RANK = { Nulo: 0, Bajo: 1, Medio: 2, Alto: 3, 'Muy alto': 4 };
const LOGO_SRC = `data:image/png;base64,${OWL_LOGO_B64.trim()}`;
const GI_EVENTO_LABEL = { g1_1: 'Accidentes', g1_2: 'Asaltos', g1_3: 'Actos violentos', g1_4: 'Secuestro', g1_5: 'Amenazas', g1_6: 'Otros' };

// ── Utilidades de formato ────────────────────────────────────────

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function pct(n, total) { return total > 0 ? Math.round((n / total) * 100) : 0; }
function chipStyle(nivel) { return `--c:${NIVEL_COLOR[nivel]};--cs:${NIVEL_COLOR[nivel]}22`; }

// Convierte una tabla de rangos [{min,max,nivel}] (ver data/baremos.js) al formato
// de despliegue "<20 / 20–44 / … / ≥90" que usa el anexo de baremo.
function formatearRangos(rangos) {
  return rangos.map((r, i) => {
    if (r.min === r.max) return String(r.min);
    if (i === 0) return `&lt;${r.max + 1}`;
    if (i === rangos.length - 1) return `&ge;${r.min}`;
    return `${r.min}–${r.max}`;
  });
}

function media(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0; }
function desvEst(arr) {
  if (arr.length < 2) return 0;
  const m = media(arr);
  return Math.sqrt(arr.reduce((a, b) => a + (b - m) ** 2, 0) / (arr.length - 1));
}

// ── Agregaciones (mismo patrón que ResultadosPage.jsx / pdf-generator.js) ───

function countNiveles(arr) {
  const c = { Nulo: 0, Bajo: 0, Medio: 0, Alto: 0, 'Muy alto': 0 };
  arr.forEach(r => { if (r.nivel && c[r.nivel] !== undefined) c[r.nivel]++; });
  return c;
}
function countField(fichas, field, cap = 6) {
  const c = {};
  Object.values(fichas).forEach(f => { const v = f[field]; if (v && String(v).trim()) { const k = String(v).trim(); c[k] = (c[k] || 0) + 1; } });
  const entries = Object.entries(c).sort((a, b) => b[1] - a[1]);
  if (entries.length <= cap) return Object.fromEntries(entries);
  const top = entries.slice(0, cap - 1);
  const restSum = entries.slice(cap - 1).reduce((a, [, v]) => a + v, 0);
  return Object.fromEntries([...top, ['Otras', restSum]]);
}
function agregarDominios(res) {
  const a = {};
  res.forEach(r => {
    Object.entries(r.dominios || {}).forEach(([k, d]) => {
      if (!a[k]) a[k] = { puntajes: [], niveles: { Nulo: 0, Bajo: 0, Medio: 0, Alto: 0, 'Muy alto': 0 }, cat: d.categoria || '' };
      a[k].puntajes.push(d.puntaje || 0);
      if (d.nivel && a[k].niveles[d.nivel] !== undefined) a[k].niveles[d.nivel]++;
    });
  });
  return a;
}
function agregarCategorias(res) {
  const a = {};
  res.forEach(r => {
    Object.entries(r.categorias || {}).forEach(([k, d]) => {
      if (!a[k]) a[k] = { puntajes: [], niveles: { Nulo: 0, Bajo: 0, Medio: 0, Alto: 0, 'Muy alto': 0 } };
      a[k].puntajes.push(d.puntaje || 0);
      if (d.nivel && a[k].niveles[d.nivel] !== undefined) a[k].niveles[d.nivel]++;
    });
  });
  return a;
}

// ── Fragmentos visuales reutilizables ────────────────────────────

function donutSVG(dist, total) {
  const R = 80, SW = 30, SIZE = 190, GAP = 1.5;
  const C = 2 * Math.PI * R;
  let cum = 0;
  const arcos = NIVEL_ARR.map(n => {
    const cnt = dist[n] || 0;
    if (!cnt) return '';
    const rawLen = (cnt / total) * C;
    const offset = -cum;
    cum += rawLen;
    const len = Math.max(rawLen - GAP, 0);
    return `<circle cx="95.0" cy="95.0" r="${R.toFixed(3)}" fill="none" stroke="${NIVEL_COLOR[n]}" stroke-width="${SW}" stroke-dasharray="${len.toFixed(3)} ${C.toFixed(3)}" stroke-dashoffset="${offset.toFixed(3)}" stroke-linecap="butt"/>`;
  }).join('');
  return `<svg viewBox="0 0 ${SIZE} ${SIZE}" width="${SIZE}" height="${SIZE}" style="transform:rotate(-90deg)">
    <circle cx="95.0" cy="95.0" r="${R.toFixed(3)}" fill="none" stroke="#ECE7DC" stroke-width="${SW}"/>
    ${arcos}
    <g style="transform:rotate(90deg);transform-origin:center">
      <text x="95.0" y="91.0" text-anchor="middle" class="donut-num">${total}</text>
      <text x="95.0" y="110.0" text-anchor="middle" class="donut-lbl">evaluados</text>
    </g>
  </svg>`;
}

function stackBar(niveles, total, height = 12) {
  const spans = NIVEL_ARR.map(n => {
    const p = pct(niveles[n] || 0, total);
    if (p <= 0) return '';
    return `<span style="width:${p}%;background:${NIVEL_COLOR[n]}"></span>`;
  }).join('');
  return `<div class="stack" style="height:${height}px">${spans}</div>`;
}
function distMini(niveles) {
  return `<div class="dist-mini">${NIVEL_ARR.map(n => `<div><b>${niveles[n] || 0}</b>${n}</div>`).join('')}</div>`;
}
function footer(nEmpresa, nCentro, fh, pageNo) {
  return `<div class="footer"><span class="conf"><span class="owl-sm"></span>Documento confidencial &middot; Numeral 7.7</span><span>Informe NOM-035-STPS-2018 &middot; ${esc(nCentro)} &middot; ${esc(fh)}</span><span class="pageno">${pageNo}</span></div>`;
}
function secHead(num, eyebrow, titulo) {
  return `<div class="sec-head"><div class="sec-num">${num}</div><div class="sec-tt"><div class="eyebrow">${esc(eyebrow)}</div><h2>${esc(titulo)}</h2></div></div>`;
}

// Contador de numeral romano dinámico (solo avanza cuando una sección con
// contenido real se emite — igual que el conteo dinámico de pdf-generator.js).
function crearContadorRomano() {
  const romanos = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
  let i = 0;
  return () => romanos[i++] || String(i);
}

function domCard(nombre, d, total, rangos) {
  const pts = d.puntajes;
  const m = media(pts), sd = desvEst(pts);
  const mn = pts.length ? Math.min(...pts) : 0;
  const mx = pts.length ? Math.max(...pts) : 0;
  const nivel = determinarNivel(Math.round(m), rangos);
  const desc = DOM_INTERPRETACION[nombre] || DOM_INTERPRETACION[nombre.replace('desempeño', 'desempeno')] || '';
  return `<div class="domcard">
    <div class="cat">${esc(d.cat)}</div>
    <div class="domcard-h"><h3>${esc(nombre)}</h3><span class="chip" style="${chipStyle(nivel)}">${nivel}</span></div>
    <div class="desc">${esc(desc)}</div>
    <div class="domcard-grid">
      <div>${stackBar(d.niveles, total)}${distMini(d.niveles)}</div>
      <div><div class="statrow">
        <div class="stat"><span class="stat-k">N</span><span class="stat-v">${total}</span></div>
        <div class="stat"><span class="stat-k">Media</span><span class="stat-v">${m.toFixed(1)}</span></div>
        <div class="stat"><span class="stat-k">D.E.</span><span class="stat-v">${sd.toFixed(1)}</span></div>
        <div class="stat"><span class="stat-k">Mín</span><span class="stat-v">${mn}</span></div>
        <div class="stat"><span class="stat-k">Máx</span><span class="stat-v">${mx}</span></div>
      </div></div>
    </div>
  </div>`;
}

// ── CSS (idéntico al bosquejo aprobado, con el logo deduplicado y overflow
// visible como red de seguridad en la paginación por lotes) ─────────────

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Libre+Franklin:wght@300;400;500;600;700&display=swap');

:root{
  --ink:#16202E; --ink-soft:#3A4453; --muted:#7A7264; --paper:#FBFAF6;
  --paper-2:#F4F1E8; --line:#E2DDD1; --line-2:#CFC9BA;
  --accent:#0E6E6B; --accent-deep:#0A4E4C; --gold:#B8893A;
  --nulo:#3E8E6E; --bajo:#88A23F; --medio:#D9A441; --alto:#C9722E; --muyalto:#B23A36;
}
/* Se desactivan ligaduras/alternativas contextuales como precaución general
   (no tiene costo visual). La razón real por la que se cambió Fraunces por
   Playfair Display abajo: el archivo de fuente de Fraunces específicamente
   produce un PDF que se ve bien pero cuya capa de texto pierde letras ('n',
   'm', 's') al extraerla — se confirmó con dos parsers de PDF independientes
   y comparando contra otras fuentes (serif del sistema, Playfair Display,
   Libre Franklin), que no tienen el problema. Un documento de cumplimiento
   necesita texto real seleccionable/buscable, no solo una imagen bien dibujada. */
*{box-sizing:border-box;margin:0;padding:0;
  font-variant-ligatures:none;
  font-feature-settings:"liga" 0,"dlig" 0,"clig" 0,"calt" 0,"hlig" 0;}
@page{size:Letter;margin:0}
html{-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:'Libre Franklin',sans-serif;color:var(--ink);background:#d8d4c8;
  font-size:9.6pt;line-height:1.5;-webkit-font-smoothing:antialiased}

.page{position:relative;width:8.5in;min-height:11in;background:var(--paper);
  overflow:visible;padding:16mm 16mm 18mm 16mm;page-break-after:always}
.page:last-child{page-break-after:auto}

.serif{font-family:'Playfair Display',serif}
h1,h2,h3{font-family:'Playfair Display',serif;font-weight:500;color:var(--ink);line-height:1.1}
.eyebrow{font-size:7.4pt;letter-spacing:.22em;text-transform:uppercase;color:var(--accent);font-weight:600}
.muted{color:var(--muted)}
em{font-style:normal;color:var(--muted)}

.footer{position:absolute;left:16mm;right:16mm;bottom:9mm;display:flex;
  justify-content:space-between;align-items:center;font-size:6.8pt;
  color:var(--muted);letter-spacing:.04em;border-top:.5px solid var(--line);padding-top:5px}
.footer .conf{text-transform:uppercase;letter-spacing:.14em;display:inline-flex;align-items:center}
.owl-sm{display:inline-block;width:15px;height:11px;margin-right:6px;opacity:.5;
  vertical-align:middle;background:url('${LOGO_SRC}') center/contain no-repeat}
.pageno{font-family:'Playfair Display',serif;font-size:8.5pt;color:var(--ink-soft)}

.sec-head{display:flex;align-items:flex-start;gap:14px;margin-bottom:14px;
  padding-bottom:11px;border-bottom:1.5px solid var(--ink)}
.sec-num{font-family:'Playfair Display',serif;font-size:30pt;font-weight:300;line-height:.8;color:var(--accent);min-width:42px}
.sec-tt h2{font-size:17pt}
.sec-tt .eyebrow{margin-bottom:3px}

.cover{padding:0}
.cover-panel{position:absolute;inset:13mm;border:1px solid var(--line-2);display:flex;flex-direction:column}
.cover-top{padding:13mm 14mm 0 14mm;display:flex;justify-content:space-between;align-items:flex-start}
.brand-lockup{display:flex;align-items:center;gap:13px}
.brand-lockup .owl{height:46px;width:auto;display:block}
.brand{font-family:'Playfair Display',serif;font-size:15pt;font-weight:600;letter-spacing:.02em}
.brand .dot{color:var(--accent)}
.brand-sub{font-size:6.6pt;letter-spacing:.34em;text-transform:uppercase;color:var(--muted);margin-top:2px}
.brand-url{font-size:7pt;letter-spacing:.08em;color:var(--accent);margin-top:3px;font-weight:500}
.norm-badge{text-align:right;font-size:7pt;letter-spacing:.12em;color:var(--ink-soft);text-transform:uppercase;line-height:1.8}
.norm-badge b{font-family:'Playfair Display',serif;font-size:9.5pt;letter-spacing:.04em}
.cover-mid{flex:1;display:flex;flex-direction:column;justify-content:center;padding:0 14mm}
.cover-kicker{font-size:8pt;letter-spacing:.28em;text-transform:uppercase;color:var(--accent);font-weight:600;margin-bottom:14px}
.cover-title{font-family:'Playfair Display',serif;font-size:40pt;font-weight:500;line-height:1.02;letter-spacing:-.01em;color:var(--ink)}
.cover-title .lite{font-weight:300;font-style:italic;color:var(--ink-soft)}
.cover-rule{width:64px;height:3px;background:var(--accent);margin:22px 0}
.cover-org{font-size:11.5pt;color:var(--ink-soft);max-width:78%}
.cover-org b{color:var(--ink);font-weight:600}
.cover-bottom{border-top:1px solid var(--line);margin:0 14mm;padding:11mm 0 13mm 0;display:flex;justify-content:space-between;align-items:flex-end}
.cover-meta{display:flex;gap:34px}
.cm-k{font-size:6.6pt;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
.cm-v{font-family:'Playfair Display',serif;font-size:11pt;color:var(--ink);margin-top:3px}
.valoracion{text-align:right}
.val-tag{font-size:6.6pt;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
.val-pill{display:inline-block;margin-top:5px;padding:7px 16px;border-radius:2px;font-family:'Playfair Display',serif;font-size:13pt;font-weight:600;color:#fff}

.dl{display:grid;grid-template-columns:150px 1fr;row-gap:0}
.dl dt{font-size:7.2pt;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);padding:8px 0;border-bottom:.5px solid var(--line)}
.dl dd{font-size:9.6pt;color:var(--ink);padding:8px 0;border-bottom:.5px solid var(--line)}
.dl dd b{font-weight:600}

.kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:11px;margin-top:6px}
.kpi{background:var(--paper-2);border:.5px solid var(--line);padding:13px 14px;border-radius:2px}
.kpi-n{font-family:'Playfair Display',serif;font-size:23pt;font-weight:500;line-height:1;color:var(--accent-deep)}
.kpi-l{font-size:7.4pt;letter-spacing:.06em;color:var(--muted);margin-top:6px;text-transform:uppercase}

.guia-list{margin-top:4px}
.guia-item{display:flex;gap:12px;padding:11px 0;border-bottom:.5px solid var(--line)}
.guia-item:last-child{border-bottom:none}
.guia-tag{font-family:'Playfair Display',serif;font-size:12pt;font-weight:600;color:var(--accent);min-width:30px}
.guia-item p{font-size:8.8pt;color:var(--ink-soft);line-height:1.45}
.guia-item p b{color:var(--ink)}

.callout{border-left:3px solid var(--c,var(--accent));background:var(--cs,#fff);padding:11px 14px;border-radius:0 2px 2px 0;margin:12px 0}
.callout .co-tag{font-size:7pt;letter-spacing:.16em;text-transform:uppercase;font-weight:700;color:var(--c,var(--accent))}
.callout p{font-size:8.8pt;color:var(--ink-soft);margin-top:3px;line-height:1.45}
.callout p b{color:var(--ink)}

.chip{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:7.6pt;font-weight:600;color:var(--c);background:var(--cs);letter-spacing:.02em}
.chip::before{content:"";width:6px;height:6px;border-radius:50%;background:var(--c)}
.chip--sm{padding:2px 8px;font-size:7pt}

.donut-wrap{display:flex;align-items:center;gap:26px}
.donut-num{font-family:'Libre Franklin';font-size:26px;font-weight:700;fill:var(--ink)}
.donut-lbl{font-family:'Libre Franklin';font-size:8.5px;font-weight:500;fill:var(--muted);letter-spacing:.08em;text-transform:uppercase}
.legend{display:flex;flex-direction:column;gap:7px}
.leg{display:flex;align-items:center;gap:9px;font-size:8.6pt}
.leg .sw{width:11px;height:11px;border-radius:2px}
.leg .lt{flex:1;color:var(--ink-soft)}
.leg .lv{font-family:'Playfair Display',serif;font-weight:600;color:var(--ink);min-width:54px;text-align:right}
.leg .lp{color:var(--muted);min-width:34px;text-align:right;font-size:7.8pt}

.stack{display:flex;width:100%;border-radius:3px;overflow:hidden;background:#ECE7DC}
.stack span{display:block;height:100%}

.domtable{width:100%;border-collapse:collapse;margin-top:4px}
.domtable th{font-size:6.8pt;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);text-align:left;padding:0 0 7px 0;border-bottom:1px solid var(--ink);font-weight:600}
.domtable th.r,.domtable td.r{text-align:right}
.domtable td{padding:8px 0;border-bottom:.5px solid var(--line);vertical-align:middle}
.domtable .dn{font-weight:500;color:var(--ink);font-size:8.8pt}
.domtable .dc{font-size:7pt;color:var(--muted);letter-spacing:.04em}
.domtable .bar-cell{width:150px}
.domtable .mean{font-family:'Playfair Display',serif;font-weight:600;font-size:9.5pt}

.domcard{border:.5px solid var(--line);border-radius:3px;padding:10px 14px;margin-bottom:8px;background:#fff;break-inside:avoid}
.domcard-h{display:flex;justify-content:space-between;align-items:baseline;gap:12px}
.domcard-h h3{font-size:11pt;font-weight:600}
.domcard .cat{font-size:6.8pt;letter-spacing:.1em;text-transform:uppercase;color:var(--accent);font-weight:600;margin-bottom:2px}
.domcard .desc{font-size:8pt;color:var(--muted);line-height:1.38;margin:4px 0 6px 0}
.domcard-grid{display:grid;grid-template-columns:1fr 200px;gap:18px;align-items:center}
.statrow{display:flex;gap:0;margin-top:7px;border-top:.5px solid var(--line);padding-top:6px}
.stat{flex:1;text-align:center;border-right:.5px solid var(--line)}
.stat:last-child{border-right:none}
.stat-k{display:block;font-size:6.4pt;letter-spacing:.08em;text-transform:uppercase;color:var(--muted)}
.stat-v{display:block;font-family:'Playfair Display',serif;font-size:11pt;font-weight:600;margin-top:1px}
.dist-mini{display:flex;justify-content:space-between;margin-top:7px;font-size:6.8pt;color:var(--muted)}
.dist-mini b{font-family:'Playfair Display',serif;color:var(--ink);font-size:8.5pt;display:block;text-align:center}

.demo-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 28px}
.demo-block .demo-tt{font-size:7.4pt;letter-spacing:.1em;text-transform:uppercase;color:var(--accent);font-weight:600;padding-bottom:6px;margin-bottom:6px;border-bottom:.5px solid var(--line)}
.demorow{display:flex;align-items:center;gap:8px;padding:2.5px 0}
.demo-l{font-size:8.2pt;color:var(--ink-soft);min-width:96px}
.demo-bar{flex:1;height:7px;background:#ECE7DC;border-radius:4px;overflow:hidden}
.demo-bar span{display:block;height:100%;background:var(--accent);border-radius:4px}
.demo-v{font-size:7.8pt;color:var(--ink);min-width:54px;text-align:right;font-weight:500}

.prog{border-collapse:collapse;width:100%;margin-top:4px}
.prog th{font-size:6.8pt;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);text-align:left;padding:0 8px 7px 8px;border-bottom:1px solid var(--ink);font-weight:600}
.prog td{padding:10px 8px;border-bottom:.5px solid var(--line);vertical-align:top;font-size:8.4pt}
.prog td:first-child{padding-left:0}
.prog .pdom{font-weight:600;color:var(--ink);font-size:9pt}
.prog .pacc{color:var(--ink-soft);line-height:1.4}
.prog .presp{color:var(--muted);font-size:7.8pt}

.semaforo{border-collapse:collapse;width:100%;margin-top:6px}
.semaforo th{font-size:6.8pt;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);text-align:left;padding:0 0 8px 0;border-bottom:1px solid var(--ink);font-weight:600}
.semaforo th.r,.semaforo td.r{text-align:right}
.semaforo td{padding:9px 0;border-bottom:.5px solid var(--line);font-size:9pt}
.semaforo .sdot{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:8px;vertical-align:middle}
.semaforo .sval{font-family:'Playfair Display',serif;font-weight:600}

.baremo{border-collapse:collapse;width:100%;margin-top:8px;font-size:8pt}
.baremo th{padding:7px 8px;color:#fff;font-weight:600;font-size:7.6pt;text-align:center}
.baremo th:first-child{text-align:left;background:var(--ink)!important}
.baremo td{padding:6px 8px;border-bottom:.5px solid var(--line);text-align:center;font-family:'Playfair Display',serif;font-size:8.2pt}
.baremo td:first-child{text-align:left;font-family:'Libre Franklin';font-weight:500;color:var(--ink-soft)}
.baremo tr:nth-child(even) td{background:var(--paper-2)}

.firma{margin-top:30px;display:flex;gap:40px;align-items:flex-end}
.firma-line{flex:1;border-top:1px solid var(--ink);padding-top:8px;max-width:300px}
.firma-line .fn{font-family:'Playfair Display',serif;font-size:11pt;font-weight:600}
.firma-line .fc{font-size:8pt;color:var(--muted);margin-top:2px}

.lead{font-size:10pt;line-height:1.6;color:var(--ink-soft)}
.lead b{color:var(--ink);font-weight:600}
p.body{font-size:9.2pt;line-height:1.55;color:var(--ink-soft);margin-top:8px}
p.body b{color:var(--ink)}
.spacer{height:12px}
.note{font-size:7.6pt;color:var(--muted);font-style:italic;margin-top:10px;line-height:1.4}
`;

// ── Constructor principal ────────────────────────────────────────

export function construirInformeHTML(dd) {
  const nEmpresa = dd.empresa?.razonSocial || dd.empresa?.nombre || 'Empresa';
  const nCentro = dd.centro?.nombreCentro || 'Centro';
  const domicilio = [dd.centro?.calle, dd.centro?.numExterior ? 'No.' + dd.centro.numExterior : '', dd.centro?.colonia ? 'Col.' + dd.centro.colonia : '', dd.centro?.municipio, dd.centro?.estado, dd.centro?.cp ? 'CP ' + dd.centro.cp : ''].filter(Boolean).join(', ');
  const fh = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  const nTrab = parseInt(dd.centro?.numTrabajadores) || 0;
  const fichas = dd.fichas || {};
  // La Ecuación 1 de muestreo de la NOM-035 solo aplica a la Guía III
  // (centros de más de 50 trabajadores) — calcularMuestraGIII regresa null
  // para centros más chicos, donde la norma exige evaluar al 100% de la
  // plantilla y no hay fórmula que citar.
  const muestraGIIIReq = calcularMuestraGIII(nTrab);
  const usaEcuacion1 = muestraGIIIReq !== null;
  const nMuestra = muestraGIIIReq || nTrab;
  const rGI = dd.rGI || [], rGII = dd.rGII || [], rGIII = dd.rGIII || [];
  const giTotal = rGI.length, giATS = rGI.filter(r => r.requiere).length;
  const totalEvaluados = Math.max(giTotal, rGII.length, rGIII.length);

  const giiC = countNiveles(rGII), giiiC = countNiveles(rGIII);
  const domAggII = agregarDominios(rGII), catAggII = agregarCategorias(rGII);
  const domAggIII = agregarDominios(rGIII), catAggIII = agregarCategorias(rGIII);

  const giEventos = { Accidentes: 0, Asaltos: 0, 'Actos violentos': 0, Secuestro: 0, Amenazas: 0, Otros: 0 };
  Object.values(dd.rGIraw || {}).forEach(r => Object.keys(GI_EVENTO_LABEL).forEach(k => { if (r[k] === true) giEventos[GI_EVENTO_LABEL[k]]++; }));

  // ¿Cuál guía de calificación (II o III) está activa para este centro?
  const activa = rGIII.length > 0 ? 'III' : rGII.length > 0 ? 'II' : null;
  const activaRes = activa === 'III' ? rGIII : rGII;
  const activaC = activa === 'III' ? giiiC : giiC;
  const activaDomAgg = activa === 'III' ? domAggIII : domAggII;
  const activaCatAgg = activa === 'III' ? catAggIII : catAggII;
  const activaNivRiesgo = activa === 'III' ? NIVELES_RIESGO_GIII : NIVELES_RIESGO_GII;
  const activaNivCat = activa === 'III' ? NIVELES_CATEGORIA_GIII : NIVELES_CATEGORIA_GII;
  const activaNivDom = activa === 'III' ? NIVELES_DOMINIO_GIII : NIVELES_DOMINIO_GII;
  const catOrder = activa === 'III' ? Object.keys(NIVELES_CATEGORIA_GIII) : Object.keys(NIVELES_CATEGORIA_GII);
  const domOrder = activa === 'III' ? Object.keys(NIVELES_DOMINIO_GIII) : Object.keys(NIVELES_DOMINIO_GII);

  const romano = crearContadorRomano();
  let pageNo = 0;
  const pages = [];
  const addPage = (bodyHtml, extraClass = '') => {
    pageNo++;
    pages.push(`<div class="page ${extraClass}">${bodyHtml}${footer(nEmpresa, nCentro, fh, pageNo)}</div>`);
  };

  // ── Portada ─────────────────────────────────────────────────
  const nivelFinal = activa ? determinarNivel(Math.round(media(activaRes.map(r => r.totalFinal || 0))), activaNivRiesgo) : 'Nulo';
  pages.push(`<div class="page cover">
    <div class="cover-panel">
      <div class="cover-top">
        <div class="brand-lockup">
          <img class="owl" src="${LOGO_SRC}">
          <div>
            <div class="brand">Perspicaz<span class="dot"> MX</span></div>
            <div class="brand-sub">Transformamos conocimiento en resultados</div>
            <div class="brand-url">perspicaz.com.mx</div>
          </div>
        </div>
        <div class="norm-badge">Norma Oficial Mexicana<br><b>NOM-035-STPS-2018</b><br>Numeral 7.7</div>
      </div>
      <div class="cover-mid">
        <div class="cover-kicker">Informe de evaluación</div>
        <div class="cover-title">Factores de riesgo<br>psicosocial <span class="lite">y entorno<br>organizacional</span></div>
        <div class="cover-rule"></div>
        <div class="cover-org"><b>${esc(nEmpresa)}</b><br>${esc(nCentro)}</div>
      </div>
      <div class="cover-bottom">
        <div class="cover-meta">
          <div><div class="cm-k">Fecha</div><div class="cm-v">${esc(fh)}</div></div>
          <div><div class="cm-k">Población</div><div class="cm-v">${nTrab} trab.</div></div>
          <div><div class="cm-k">Muestra evaluada</div><div class="cm-v">${totalEvaluados} / ${nMuestra}</div></div>
        </div>
        <div class="valoracion"><div class="val-tag">Nivel de riesgo (final)</div>
          <span class="val-pill" style="background:${NIVEL_COLOR[nivelFinal]}">${nivelFinal.toUpperCase()}</span></div>
      </div>
    </div></div>`);

  // ── I. Marco de la evaluación ──────────────────────────────
  {
    const guiaItems = [];
    if (giTotal > 0) guiaItems.push(`<div class="guia-item"><span class="guia-tag">I</span><p><b>Acontecimientos traumáticos severos.</b> 20 ítems en 4 secciones (acontecimientos, recuerdos persistentes, evitación y afectación). Aplicado a ${giTotal} trabajadores.</p></div>`);
    if (rGII.length > 0) guiaItems.push(`<div class="guia-item"><span class="guia-tag">II</span><p><b>Factores de riesgo psicosocial</b> (centros de 16 a 50 trabajadores). 46 ítems que evalúan 4 categorías y 8 dominios.${activa === 'II' ? ' <em>— guía aplicada en este centro.</em>' : ''}</p></div>`);
    if (rGIII.length > 0) guiaItems.push(`<div class="guia-item"><span class="guia-tag">III</span><p><b>Factores de riesgo y entorno organizacional</b> (centros de más de 50 trabajadores). 72 ítems que evalúan 5 categorías y 10 dominios.${activa === 'III' ? ' <em>— guía aplicada en este centro.</em>' : ''}</p></div>`);

    addPage(`${secHead(romano(), 'Datos de la organización y metodología', 'Marco de la evaluación')}
    <dl class="dl">
      <dt>Razón social</dt><dd><b>${esc(nEmpresa)}</b></dd>
      <dt>RFC</dt><dd>${esc(dd.empresa?.rfc || '---')}</dd>
      <dt>Centro de trabajo</dt><dd>${esc(nCentro)}</dd>
      <dt>Actividad principal</dt><dd>${esc(dd.centro?.actividadPrincipal || dd.empresa?.actividadEconomica || '---')}</dd>
      <dt>Domicilio</dt><dd>${esc(domicilio || '---')}</dd>
    </dl>
    <div class="spacer"></div>
    <div class="kpis">
      <div class="kpi"><div class="kpi-n">${nTrab}</div><div class="kpi-l">Trabajadores en nómina</div></div>
      <div class="kpi"><div class="kpi-n">${nMuestra}</div><div class="kpi-l">${usaEcuacion1 ? 'Muestra requerida (Ec. 1)' : 'Cobertura requerida (100%)'}</div></div>
      <div class="kpi"><div class="kpi-n">${totalEvaluados}</div><div class="kpi-l">Muestra evaluada</div></div>
    </div>
    <div class="spacer"></div>
    <div class="eyebrow" style="margin-bottom:8px">Objetivo</div>
    <p class="body">Identificar y analizar los factores de riesgo psicosocial y evaluar el entorno
    organizacional en el centro de trabajo, conforme a la NOM-035-STPS-2018, con la finalidad de
    adoptar medidas preventivas, promover un entorno organizacional favorable y atender las prácticas
    opuestas al mismo y los actos de violencia laboral.</p>
    <div class="spacer"></div>
    <div class="eyebrow" style="margin-bottom:6px">Método utilizado</div>
    <p class="body" style="margin-top:4px">Aplicación digital de los instrumentos de las Guías de Referencia,
    con confidencialidad garantizada y consentimiento informado previo (numeral 7.1c).</p>
    <div class="guia-list">${guiaItems.join('')}</div>
    <div class="callout" style="--c:var(--accent);--cs:var(--paper-2)">
      <div class="co-tag">Nota metodológica</div>
      <p>${usaEcuacion1
        ? `La muestra evaluada (<b>${totalEvaluados} trabajadores</b>) cubre el <b>${pct(totalEvaluados, nMuestra)}%</b> de la muestra requerida por la Ecuación 1 de la NOM-035.`
        : `La NOM-035 exige aplicar los cuestionarios al <b>100% de la plantilla</b> en centros de hasta 50 trabajadores — la Ecuación 1 de muestreo solo aplica a centros de más de 50. La evaluación actual cubre <b>${totalEvaluados} de ${nMuestra} trabajadores</b> (<b>${pct(totalEvaluados, nMuestra)}%</b>).`
      } Todos los cuestionarios se aplicaron con confidencialidad garantizada
      y consentimiento informado previo (numeral 7.1c).</p>
    </div>`);
  }

  // ── II. Datos generales de la población evaluada ────────────
  const totalFichas = Object.keys(fichas).length;
  if (totalFichas > 0) {
    const campos = [
      ['Género', 'sexo'], ['Estado civil', 'estadoCivil'], ['Escolaridad', 'nivelEstudios'],
      ['Tipo de puesto', 'tipoPuesto'], ['Jornada', 'tipoJornada'], ['Contratación', 'tipoContratacion'],
      ['Áreas de trabajo', 'area'], ['Turno', 'turno'],
    ].map(([label, field]) => {
      let c = countField(fichas, field);
      if (field === 'nivelEstudios' && Object.keys(c).length === 0) c = countField(fichas, 'escolaridad');
      return [label, c];
    }).filter(([, c]) => Object.keys(c).length > 0);

    const bloque = ([label, c]) => `<div class="demo-block"><div class="demo-tt">${esc(label)}</div>${Object.entries(c).map(([k, v]) => `<div class="demorow"><span class="demo-l">${esc(k)}</span><span class="demo-bar"><span style="width:${pct(v, totalFichas)}%"></span></span><span class="demo-v">${v} <em>· ${pct(v, totalFichas)}%</em></span></div>`).join('')}</div>`;

    // Lotes de 6 bloques por página (patrón observado: cabe cómodo en una .page)
    for (let i = 0; i < campos.length; i += 6) {
      const lote = campos.slice(i, i + 6);
      const head = i === 0 ? secHead(romano(), 'Datos generales de la población evaluada', 'Perfil sociodemográfico y laboral') : `<div style="height:2mm"></div>`;
      const intro = i === 0 ? `<p class="body" style="margin-top:0;margin-bottom:14px">Características de los <b>${totalFichas} trabajadores</b> evaluados en ${esc(nCentro)}.</p>` : '';
      addPage(`${head}${intro}<div class="demo-grid">${lote.map(bloque).join('')}</div>`);
    }
  }

  // ── Guía de Referencia I ────────────────────────────────────
  if (giTotal > 0) {
    const giRec = rGI.filter(r => (r.resultado?.recuerdos || 0) > 0).length;
    const giEvi = rGI.filter(r => (r.resultado?.evitacion || 0) >= 3).length;
    const giAfe = rGI.filter(r => (r.resultado?.afectacion || 0) >= 2).length;
    const eventosFilas = Object.entries(giEventos).map(([k, v]) => `<tr><td class="dn">${esc(k)}</td><td class="r mean">${v}</td><td class="r muted">${pct(v, giTotal)}%</td></tr>`).join('');
    addPage(`${secHead(romano(), 'Guía de Referencia I', 'Acontecimientos traumáticos severos')}
    <p class="body" style="margin-top:0">La Guía I identifica trabajadores expuestos a
    acontecimientos traumáticos severos. Requieren atención clínica quienes experimentaron un
    acontecimiento (Sec. I) y presentan síntomas en al menos una sección subsecuente.</p>
    <div class="kpis" style="margin-top:14px">
      <div class="kpi"><div class="kpi-n">${giTotal}</div><div class="kpi-l">Trabajadores evaluados</div></div>
      <div class="kpi"><div class="kpi-n" style="color:${giATS > 0 ? '#C9722E' : 'inherit'}">${giATS}</div><div class="kpi-l">Requieren canalización</div></div>
      <div class="kpi"><div class="kpi-n">${pct(giATS, giTotal)}%</div><div class="kpi-l">del total evaluado</div></div>
    </div>
    <div class="spacer"></div>
    <div style="display:grid;grid-template-columns:1fr 1.25fr;gap:26px">
      <div><div class="eyebrow" style="margin-bottom:6px">Acontecimientos reportados</div>
        <table class="domtable"><thead><tr><th>Tipo</th><th class="r">Trab.</th><th class="r">%</th></tr></thead>
        <tbody>${eventosFilas}</tbody></table></div>
      <div><div class="eyebrow" style="margin-bottom:6px">Análisis de síntomas asociados</div>
        <table class="domtable"><thead><tr><th>Sección</th><th>Criterio NOM-035</th><th class="r">Trab.</th><th class="r">%</th></tr></thead>
        <tbody>
        <tr><td class="dn">II. Recuerdos persistentes</td><td class="dc">&gt;=1 respuesta afirmativa</td><td class="r mean">${giRec}</td><td class="r muted">${pct(giRec, giTotal)}%</td></tr>
        <tr><td class="dn">III. Conductas de evitación</td><td class="dc">&gt;=3 respuestas afirmativas</td><td class="r mean">${giEvi}</td><td class="r muted">${pct(giEvi, giTotal)}%</td></tr>
        <tr><td class="dn">IV. Afectación</td><td class="dc">&gt;=2 respuestas afirmativas</td><td class="r mean">${giAfe}</td><td class="r muted">${pct(giAfe, giTotal)}%</td></tr>
        </tbody></table></div>
    </div>
    ${giATS > 0 ? `<div class="callout" style="--c:#C9722E;--cs:#F7E7D9">
      <div class="co-tag">Atención requerida</div>
      <p><b>${giATS} trabajador${giATS !== 1 ? 'es' : ''}</b> requiere${giATS !== 1 ? 'n' : ''} valoración clínica especializada por cumplir
      los criterios del numeral 7.2. Deberán ser canalizados al servicio de salud dentro de los
      <b>30 días hábiles</b> siguientes a su identificación, con seguimiento documentado de las acciones realizadas.</p>
    </div>` : `<div class="callout" style="--c:var(--nulo);--cs:#EEF1E3">
      <div class="co-tag">Resultado favorable</div>
      <p>Ningún trabajador cumple con los criterios para requerir atención clínica especializada conforme a la Guía de Referencia I.</p>
    </div>`}`);
  }

  // ── Resultado general del cuestionario activo (II o III) ────────
  if (activa) {
    const dist = activaC;
    const nMedia = media(activaRes.map(r => r.totalFinal || 0));
    const nivelMedia = determinarNivel(Math.round(nMedia), activaNivRiesgo);
    const amPct = pct((dist.Alto || 0) + (dist['Muy alto'] || 0), activaRes.length);

    const catRows = catOrder.filter(c => activaCatAgg[c]).map(cat => {
      const d = activaCatAgg[cat];
      const n = d.puntajes.length;
      const m = media(d.puntajes);
      const nivel = determinarNivel(Math.round(m), activaNivCat[cat]);
      const domsDeCat = domOrder.filter(dn => activaDomAgg[dn] && activaDomAgg[dn].cat === cat);
      return `<tr><td><span class="dn">${esc(cat)}</span><br><span class="dc">${esc(domsDeCat.join(' · '))}</span></td><td class="bar-cell">${stackBar(d.niveles, n, 9)}</td><td class="r mean">${m.toFixed(1)}</td><td class="r"><span class="chip chip--sm" style="${chipStyle(nivel)}">${nivel}</span></td></tr>`;
    }).join('');

    addPage(`${secHead(romano(), `Guía de Referencia ${activa}`, 'Resultado general del cuestionario')}
    <div class="donut-wrap" style="margin:6px 0 4px 0">
      <div>${donutSVG(dist, activaRes.length)}</div>
      <div style="flex:1"><div class="legend">${NIVEL_ARR.map(n => `<div class="leg"><span class="sw" style="background:${NIVEL_COLOR[n]}"></span><span class="lt">${n}</span><span class="lv">${dist[n] || 0}</span><span class="lp">${pct(dist[n] || 0, activaRes.length)}%</span></div>`).join('')}</div></div>
      <div style="flex:0 0 168px;border-left:.5px solid var(--line);padding-left:22px">
        <div class="cm-k">Puntuación media</div>
        <div class="serif" style="font-size:30pt;font-weight:500;color:var(--accent-deep);line-height:1">${nMedia.toFixed(1)}</div>
        <div style="margin-top:8px"><span class="chip" style="${chipStyle(nivelMedia)}">${nivelMedia}</span></div>
        <div class="cm-k" style="margin-top:16px">En riesgo Alto / Muy alto</div>
        <div class="serif" style="font-size:24pt;font-weight:500;color:#C9722E;line-height:1.1">${amPct}%</div>
        <div class="muted" style="font-size:7.6pt">${(dist.Alto || 0) + (dist['Muy alto'] || 0)} de ${activaRes.length} trabajadores</div>
      </div>
    </div>
    <div class="eyebrow" style="margin:16px 0 3px 0">Análisis por categoría</div>
    <p class="body" style="margin-top:0;margin-bottom:8px;font-size:8.4pt">La norma califica en tres niveles: final del cuestionario (arriba), por categoría (esta tabla) y por dominio (sección siguiente).</p>
    <table class="domtable"><thead><tr><th>Categoría</th><th>Distribución por nivel</th><th class="r">Ccat</th><th class="r">Nivel</th></tr></thead><tbody>${catRows}</tbody></table>`);

    // Detalle por dominio: lotes de 5 tarjetas por página (patrón observado)
    const domsFiltrados = domOrder.filter(dn => activaDomAgg[dn]);
    const secNumDetalle = romano();
    for (let i = 0; i < domsFiltrados.length; i += 5) {
      const lote = domsFiltrados.slice(i, i + 5);
      const head = i === 0
        ? secHead(secNumDetalle, `Guía de Referencia ${activa} · Detalle`, 'Análisis por dominio')
        : `<div style="height:2mm"></div>`;
      const intro = i === 0 ? `<p class="body" style="margin-top:0;margin-bottom:9px">Resultados clasificados en cinco niveles. El nivel de cada dominio corresponde a su calificación contra el baremo de la norma (ver Anexo).</p>` : '';
      addPage(`${head}${intro}${lote.map(dn => domCard(dn, activaDomAgg[dn], activaDomAgg[dn].puntajes.length, activaNivDom[dn])).join('')}`);
    }
  }

  // ── Programa de intervención (numerales 8.3-8.5) ─────────────
  const acciones = (dd.programa || {}).acciones || [];
  if (acciones.length > 0) {
    const fila = a => `<tr><td class="pdom">${esc(a.dominio || 'Sin especificar')}${a.nivelDetectado ? `<br><span class="chip chip--sm" style="${chipStyle(a.nivelDetectado)}">${esc(a.nivelDetectado)}</span>` : ''}</td><td class="presp">${esc(a.nivelIntervencion || '---')}</td><td class="pacc">${esc(a.accion || '---')}</td><td class="presp">${esc(a.responsable || '---')}<br><em>${esc(a.estatus || 'Pendiente')}</em></td></tr>`;
    const secNumPrograma = romano();
    for (let i = 0; i < acciones.length; i += 6) {
      const lote = acciones.slice(i, i + 6);
      const head = i === 0
        ? secHead(secNumPrograma, 'Numerales 8.3, 8.4 y 8.5', 'Programa de intervención')
        : `<div style="height:2mm"></div>`;
      const intro = i === 0 ? `<p class="body" style="margin-top:0;margin-bottom:8px">Acciones definidas para atender los factores de riesgo identificados, con nivel de intervención, responsable y estatus de seguimiento.</p>` : '';
      addPage(`${head}${intro}<table class="prog"><thead><tr><th>Dominio · Nivel</th><th>Nivel de intervención</th><th>Acción a implementar</th><th>Responsable</th></tr></thead><tbody>${lote.map(fila).join('')}</tbody></table>`);
    }
  }

  // ── Resumen ejecutivo (semáforo + narrativa) ──────────────────
  if (activa) {
    const domGlobal = domOrder.filter(dn => activaDomAgg[dn]).map(dn => {
      const d = activaDomAgg[dn];
      const m = media(d.puntajes);
      const nivel = determinarNivel(Math.round(m), activaNivDom[dn]);
      return { nombre: dn, media: m, nivel };
    }).sort((a, b) => NIVEL_RANK[b.nivel] - NIVEL_RANK[a.nivel] || b.media - a.media);

    const filasSemaforo = domGlobal.map(d => `<tr><td><span class="sdot" style="background:${NIVEL_COLOR[d.nivel]}"></span>${esc(d.nombre)}</td><td class="r sval">${d.media.toFixed(1)}</td><td class="r" style="color:${NIVEL_COLOR[d.nivel]};font-weight:600">${d.nivel}</td></tr>`).join('');

    const amPct = pct((activaC.Alto || 0) + (activaC['Muy alto'] || 0), activaRes.length);
    const nDomAM = domGlobal.filter(d => d.nivel === 'Alto' || d.nivel === 'Muy alto').length;
    const nDomTotal = domGlobal.length;
    const nivelGlobal = determinarNivel(Math.round(media(activaRes.map(r => r.totalFinal || 0))), activaNivRiesgo);

    let narrativa;
    if (amPct >= 50) narrativa = `Los resultados revelan una situación que demanda atención inmediata y sistemática. Con el ${amPct}% de la población evaluada en niveles de riesgo Alto o Muy alto, la organización enfrenta un escenario donde la mayoría de los trabajadores está expuesta a condiciones laborales que pueden afectar su salud, desempeño y permanencia.`;
    else if (amPct >= 30) narrativa = `El diagnóstico identifica áreas significativas de riesgo que requieren atención prioritaria. El ${amPct}% de los trabajadores en niveles Alto/Muy alto representa una proporción considerable que, de no atenderse, puede escalar a una situación crítica.`;
    else if (amPct > 0) narrativa = `El entorno organizacional presenta condiciones mayoritariamente favorables. Se identifican áreas puntuales de mejora que merecen atención para fortalecer las condiciones laborales y prevenir el deterioro de los indicadores.`;
    else narrativa = `El diagnóstico refleja un entorno organizacional saludable, con la totalidad de los dominios en niveles favorables. Se recomienda mantener las buenas prácticas identificadas y realizar la evaluación de seguimiento en los plazos establecidos.`;
    if (giATS > 0) narrativa += ` Adicionalmente, la evaluación de acontecimientos traumáticos severos identificó <b>${giATS} trabajador${giATS !== 1 ? 'es' : ''}</b> que requiere${giATS !== 1 ? 'n' : ''} canalización clínica, a atender de forma independiente y prioritaria conforme al numeral 7.2.`;

    const domFav = domGlobal.filter(d => d.nivel === 'Nulo' || d.nivel === 'Bajo').map(d => d.nombre);

    addPage(`${secHead(romano(), 'Diagnóstico', 'Resumen ejecutivo')}
    <p class="lead">El diagnóstico evaluó las condiciones del entorno organizacional y los
    factores de riesgo psicosocial conforme a la NOM-035-STPS-2018. La puntuación final media corresponde a un
    nivel <b>${nivelGlobal}</b>, y <b>${nDomAM} de ${nDomTotal} dominios</b> se ubican en
    nivel Alto o Muy alto, lo que ${nDomAM > 0 ? 'obliga a implementar un Programa de intervención conforme al numeral 8.3.' : 'no amerita medidas adicionales más allá de la difusión de la política vigente.'}</p>
    <div class="eyebrow" style="margin:18px 0 2px 0">Semáforo de riesgo por dominio</div>
    <table class="semaforo"><thead><tr><th>Dominio</th><th class="r">Cdom (media)</th><th class="r">Nivel</th></tr></thead><tbody>${filasSemaforo}</tbody></table>
    <p class="body">${narrativa}</p>
    ${domFav.length > 0 && nDomAM > 0 ? `<p class="note">Los dominios en nivel Nulo o Bajo (${esc(domFav.join(', '))}) representan fortalezas cuyas buenas prácticas pueden replicarse como modelo para los dominios desfavorables.</p>` : ''}`);
  }

  // ── Acciones de intervención por dominio (usa DOM_RECOMENDACIONES) ──
  const domPrioridad = activa ? domOrder.filter(dn => activaDomAgg[dn]).map(dn => {
    const d = activaDomAgg[dn];
    return { nombre: dn, pctAM: pct((d.niveles.Alto || 0) + (d.niveles['Muy alto'] || 0), d.puntajes.length) };
  }).filter(d => d.pctAM > 0).sort((a, b) => b.pctAM - a.pctAM) : [];

  if (domPrioridad.length > 0) {
    const bloque = d => {
      const tier = d.pctAM >= 50 ? 'urgente' : d.pctAM >= 25 ? 'alta' : 'media';
      const tierLabel = d.pctAM >= 50 ? 'Intervención urgente' : d.pctAM >= 25 ? 'Intervención prioritaria' : 'Acción preventiva';
      const tierColor = d.pctAM >= 50 ? '#B23A36' : d.pctAM >= 25 ? '#C9722E' : '#D9A441';
      const texto = (DOM_RECOMENDACIONES[d.nombre] || {})[tier];
      if (!texto) return '';
      return `<div class="callout" style="--c:${tierColor};--cs:${tierColor}18;margin:0 0 12px 0">
        <div class="co-tag">${esc(d.nombre)} &middot; ${d.pctAM}% Alto/Muy alto &middot; ${tierLabel}</div>
        <p>${esc(texto)}</p>
      </div>`;
    };
    // Lotes de 3 recomendaciones por página (párrafos largos)
    const secNumRecom = romano();
    for (let i = 0; i < domPrioridad.length; i += 3) {
      const lote = domPrioridad.slice(i, i + 3);
      const head = i === 0
        ? secHead(secNumRecom, 'Numerales 8.3, 8.4 y 8.5', 'Acciones de intervención por dominio')
        : `<div style="height:2mm"></div>`;
      const intro = i === 0 ? `<p class="body" style="margin-top:0;margin-bottom:12px">Recomendaciones específicas para los dominios con trabajadores en nivel Alto o Muy alto, priorizadas por porcentaje de población afectada.</p>` : '';
      addPage(`${head}${intro}${lote.map(bloque).join('')}`);
    }
  }

  // ── Conclusiones, recomendaciones y firma ────────────────────
  {
    const domGlobal = domPrioridad;

    addPage(`${secHead(romano(), 'Cierre del diagnóstico', 'Conclusiones y recomendaciones')}
    ${giTotal > 0 ? `<div class="eyebrow" style="margin-bottom:4px">Guía I · Acontecimientos traumáticos severos</div>
    <p class="body" style="margin-top:2px">${giATS > 0 ? `Se identificaron ${giATS} trabajador${giATS !== 1 ? 'es' : ''} que cumplen los criterios para requerir valoración clínica especializada. Conforme al numeral 7.2, deberán ser canalizados al servicio de salud correspondiente con el seguimiento debido.` : 'Ningún trabajador cumple con los criterios para requerir atención clínica por acontecimientos traumáticos severos.'}</p>` : ''}
    ${activa ? `<div class="eyebrow" style="margin:14px 0 4px 0">Guía ${activa} · Factores de riesgo psicosocial</div>
    <p class="body" style="margin-top:2px">${domGlobal.length > 0 ? `La situación requiere la implementación de un Programa de intervención integral conforme al numeral 8.3. Los dominios que demandan intervención prioritaria son: <b>${esc(domGlobal.map(d => d.nombre).join(', '))}</b>.` : 'Los resultados son favorables en todos los dominios. Se recomienda mantener las políticas actuales y programar la evaluación de seguimiento.'}</p>` : ''}
    <div class="eyebrow" style="margin:16px 0 8px 0">Recomendaciones generales</div>
    <ol style="padding-left:18px;color:var(--ink-soft);font-size:9pt;line-height:1.7">
      <li>Difundir la política de prevención de riesgos psicosociales y los resultados generales entre los trabajadores (numeral 7.8).</li>
      <li>Implementar el Programa de intervención conforme a los numerales 8.3, 8.4 y 8.5, atendiendo los dominios prioritarios.</li>
      <li>Realizar acciones de seguimiento para verificar la eficacia de las intervenciones implementadas.</li>
      <li>Programar la próxima evaluación conforme a la periodicidad establecida en la norma (cada dos años para resultados favorables).</li>
    </ol>
    <div class="firma">
      <div class="firma-line"><div class="fn">${esc(dd.responsable || 'Responsable de la evaluación')}</div>
        <div class="fc">${esc(dd.cargo || 'Responsable de la evaluación')}</div>
        <div class="fc">Cédula profesional: ${esc(dd.cedula || 'No aplica')}</div></div>
      <div class="firma-line"><div class="fn">&nbsp;</div>
        <div class="fc">Representante del centro de trabajo</div>
        <div class="fc">${esc(nEmpresa)}</div></div>
    </div>`);
  }

  // ── Anexo: baremo NOM-035-STPS-2018 ──────────────────────────
  if (activa) {
    const filaFinal = formatearRangos(activaNivRiesgo);
    const filasCat = catOrder.filter(c => activaNivCat[c]).map(c => `<tr><td>${esc(c)}</td>${formatearRangos(activaNivCat[c]).map(v => `<td>${v}</td>`).join('')}</tr>`).join('');
    const filasDom = domOrder.filter(d => activaNivDom[d]).map(d => `<tr><td>${esc(d)}</td>${formatearRangos(activaNivDom[d]).map(v => `<td>${v}</td>`).join('')}</tr>`).join('');
    const headNiv = `<tr><th>Resultado</th>${NIVEL_ARR.map(n => `<th style="background:${NIVEL_COLOR[n]}">${n}</th>`).join('')}</tr>`;

    addPage(`${secHead(romano(), 'Anexo', 'Baremo NOM-035-STPS-2018')}
    <p class="body" style="margin-top:0">Rangos de calificación establecidos en la norma para la clasificación de los niveles de riesgo (Guía de Referencia ${activa}).</p>
    <div class="eyebrow" style="margin:14px 0 2px 0">Calificación final del cuestionario</div>
    <table class="baremo"><thead>${headNiv}</thead><tbody><tr><td>Calificación final</td>${filaFinal.map(v => `<td>${v}</td>`).join('')}</tr></tbody></table>
    <div class="eyebrow" style="margin:14px 0 2px 0">Calificación por categoría</div>
    <table class="baremo"><thead><tr><th>Categoría</th>${NIVEL_ARR.map(n => `<th style="background:${NIVEL_COLOR[n]}">${n}</th>`).join('')}</tr></thead><tbody>${filasCat}</tbody></table>
    <div class="eyebrow" style="margin:14px 0 2px 0">Calificación por dominio</div>
    <table class="baremo"><thead><tr><th>Dominio</th>${NIVEL_ARR.map(n => `<th style="background:${NIVEL_COLOR[n]}">${n}</th>`).join('')}</tr></thead><tbody>${filasDom}</tbody></table>
    <p class="note">Los rangos reflejan los puntos de corte establecidos en las Tablas de la NOM-035-STPS-2018.
    Documento generado mediante aplicación digital con consentimiento informado, conforme al
    numeral 7.7 de la norma. La información contenida es confidencial y de uso exclusivo del centro de trabajo.</p>`);
  }

  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Informe NOM-035</title><style>${CSS}</style></head><body>${pages.join('')}</body></html>`;
}
